<template>
  <div class="video-player-container">
    <div class="video-header">
      <div class="video-title">实时监控</div>
    </div>
    <div class="video-wrapper" v-if="videoUrl">
      <video ref="videoPlayer" class="video-player" autoplay playsinline :controls="true">
        您的浏览器不支持视频播放
      </video>
    </div>
    <div class="video-empty" v-else>
      <div class="empty-icon">📹</div>
      <div class="empty-text">暂无视频流</div>
    </div>

    <!-- 对讲按钮 -->
    <div
      class="duijiang"
      :class="{ duijiang1: !reqflg, duijiang2: reqflg }"
      @mousedown="startRecord"
      @mouseup="stopRecord"
      @touchstart="startRecord"
      @touchend="stopRecord"
    ></div>
  </div>
</template>

<script>
import flvjs from "flv.js";
import { reSampleAndEncodeAudioData } from "@/utils/encode";
import { dashboardAPI } from "@/api/dashboard";

export default {
  name: "VideoPlayer",
  props: {
    videoUrl: {
      default: "",
    },
    // 添加对讲相关props
    radioId: {
      default: "",
    },
    // 添加楼层列表prop，来自getCommunityInfo接口的door_list
    doorList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      flvPlayer: null,
      // 对讲相关数据
      audioContext: null,
      mediaStream: null,
      audioSourceNode: null,
      processorNode: null,
      dataList: [],
      reqflg: false,
      ratio: 1,
      base64Audio: "",
    };
  },
  watch: {
    videoUrl: {
      handler(newUrl) {
        if (newUrl) {
          this.$nextTick(() => {
            this.initFlvPlayer();
          });
        } else {
          this.destroyFlvPlayer();
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    // 销毁flv播放器
    this.destroyFlvPlayer();
    // 停止对讲录音
    if (this.reqflg) {
      this.stopRecord();
    }
  },
  methods: {
    // 初始化flv播放器
    initFlvPlayer() {
      if (!this.videoUrl) return;

      this.$nextTick(() => {
        const videoElement = this.$refs.videoPlayer;
        if (!videoElement) return;

        // 先销毁旧的播放器
        this.destroyFlvPlayer();

        // 检查是否支持flv
        if (flvjs.isSupported()) {
          this.flvPlayer = flvjs.createPlayer(
            {
              type: "flv",
              isLive: true,
              url: this.videoUrl,
            },
            {
              enableWorker: false,
              enableStashBuffer: false,
              stashInitialSize: 128,
              lazyLoad: false,
            }
          );

          this.flvPlayer.attachMediaElement(videoElement);
          this.flvPlayer.load();

          // 使用Promise处理play，避免中断错误
          const playPromise = this.flvPlayer.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.log("自动播放被阻止，等待用户交互:", error);
            });
          }

          // 错误处理
          this.flvPlayer.on(flvjs.Events.ERROR, (errType, errDetail) => {
            console.error("flv播放错误:", errType, errDetail);
          });
        } else {
          console.warn("当前浏览器不支持flv.js");
          // 降级使用原生video
          videoElement.src = this.videoUrl;
          videoElement.play().catch((e) => console.log("播放被阻止:", e));
        }
      });
    },

    // 销毁flv播放器
    destroyFlvPlayer() {
      if (this.flvPlayer) {
        this.flvPlayer.pause();
        this.flvPlayer.unload();
        this.flvPlayer.detachMediaElement();
        this.flvPlayer.destroy();
        this.flvPlayer = null;
      }
    },

    //***  对讲开始 */
    // 获取媒体流
    getMediaStream() {
      // 大部分浏览器有安全限制，http 协议访问的页面 navigator.mediaDevices 也是 undefined， 仅提供 https 服务的可以忽略这个问题
      if (!navigator.mediaDevices) {
        return Promise.reject(
          "您的浏览器不支持获取用户设备，无法使用对讲功能，建议使用 Chrome 浏览器 ( 版本 >= 74 )"
        );
      }
      return navigator.mediaDevices
        .getUserMedia({ audio: true })
        .catch(() =>
          Promise.reject(
            "请确保录音设备正常，并允许浏览器获取录音权限，否则无法使用对讲功能"
          )
        );
    },

    // 确保音频上下文运行
    makeAudioContextRunning() {
      // 一般不会出现这种情况，以防万一
      if (!this.audioContext || this.audioContext.state === "closed") {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        return this.audioContext.resume();
      }
      return Promise.resolve();
    },

    // 开始录音
    startRecord() {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      return this.getMediaStream()
        .then((stream) => {
          this.mediaStream = stream;
          this.reqflg = true;
          return this.makeAudioContextRunning();
        })
        .then(() => {
          try {
            this.audioSourceNode = this.audioContext.createMediaStreamSource(
              this.mediaStream
            );
            // 只采集单声道方便处理数据
            this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
            this.audioSourceNode.connect(this.processorNode);
            // sampleRate = 8k & bufferSize = 4096 => 每 0.512s 触发一次 onaudioprocess
            this.processorNode.onaudioprocess = this.onAudioProcess.bind(this);
            this.processorNode.connect(this.audioContext.destination);
          } catch (e) {
            throw new Error(
              "您的浏览器音频相关接口异常，无法使用对讲功能，建议使用 Chrome 浏览器 ( 版本 >= 74 )"
            );
          }
        })
        .catch((error) => {
          console.error("开始对讲失败:", error);
          this.reqflg = false;
          this.$message && this.$message.error(error.message || "开始对讲失败");
        });
    },

    // 音频处理函数
    onAudioProcess(event) {
      const channelData = event.inputBuffer.getChannelData(0);
      this.dataList.push(channelData.slice());

      // 每 (bufferSize * ratio / audioContext.sampleRate ～ bufferSize / audioSampleRate = 4096 / 8k = 0.512s) 发送一次数据
      if (this.dataList.length >= this.ratio) {
        this.sendAudioData(this.dataList);
        this.dataList = [];
      }
    },

    // 检测是否有声音
    hasSound(dataList) {
      const thredhold = 0.01;
      return dataList.some((buffer) =>
        buffer.some((sample) => Math.abs(sample) > thredhold)
      );
    },

    // 发送音频数据
    sendAudioData(dataList1) {
      if (this.hasSound(dataList1)) {
        const base64Audio1 = reSampleAndEncodeAudioData(dataList1, 4096, this.ratio);
        // base64Audio.value += base64Audio1;
        // TODO post data
        console.log("发送对讲请求");
        dashboardAPI.voiceIntercom({ id: this.radioId, audio: base64Audio1 }).then(res => {
          // reqflg.value = false;
          this.base64Audio = "";
        });
      } else {
        console.log("未检测到有效声音，不发送数据");
      }
    },

    // 停止录音
    stopRecord() {
      this.releaseResources();
      this.dataList = [];
      if (this.audioContext) {
        this.audioContext.close().then((res) => {
          console.log("音频上下文已关闭", res);
        });
      }
      console.log("停止录音");
      this.reqflg = false;
      if (this.dataList.length) {
        this.sendAudioData(this.dataList);
        this.dataList = [];
      }
    },

    // 释放资源
    releaseResources() {
      if (this.audioSourceNode) {
        this.audioSourceNode.disconnect();
        this.audioSourceNode = null;
      }
      if (this.processorNode) {
        this.processorNode.disconnect();
        this.processorNode = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getAudioTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }
    },

    // 确认楼层选择 - 触发父组件事件，通知楼层变更
    confirmFloorSelection() {
      // 这个方法现在在父组件中处理
      this.$emit("floor-change", this.selectedDoorId);
    },
  },
};
</script>

<style scoped lang="less">
// 视频播放器样式
.video-player-container {
  width: 100%;
  height: 100%;
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  position: relative;
}

.video-header {
  text-align: center;
  margin-bottom: 10px;
}

.floor-selector {
  position: relative;
}

.custom-select {
  position: relative;
  display: inline-block;
}

.floor-dropdown {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 5px;
  color: #00d4ff;
  padding: 5px 25px 5px 10px;
  font-size: 12px;
  min-width: 100px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.floor-dropdown:hover {
  background: rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
}

.floor-dropdown:focus {
  border-color: #00d4ff;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.select-arrow {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  color: #00d4ff;
  font-size: 10px;
  pointer-events: none;
  transition: transform 0.3s ease;
}

.custom-select:hover .select-arrow {
  transform: translateY(-50%) scale(1.1);
}

.video-title {
  font-size: 16px;
  color: #00d4ff;
  text-align: center;
  font-weight: bold;
  margin-bottom: 10px;
}

.video-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #88c4ff;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 15px;
    opacity: 0.5;
  }

  .empty-text {
    font-size: 14px;
  }
}

// 楼层控制组样式
.floor-control-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.confirm-btn {
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 5px;
  color: #00d4ff;
  padding: 5px 15px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.confirm-btn:hover:not(:disabled) {
  background: rgba(0, 212, 255, 0.3);
  border-color: #00d4ff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
}

.confirm-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(0, 212, 255, 0.1);
}

.duijiang {
  width: 50px;
  height: 50px;
  z-index: 10;
  cursor: pointer;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  position: absolute;
  bottom: 20%;
  right: 20px;
  border-radius: 50%;
  overflow: hidden;
  transform: translateY(-50%);
}

.duijiang1 {
  background-image: url("@/assets/images/ht1.png");
}

.duijiang2 {
  background-image: url("@/assets/images/ht.png");
}
</style>
